/**
 * A record of which path sits at each position of the browser's history stack.
 *
 * The browser exposes how far back it can go but never what is back there, and
 * that is the one fact needed to tell a real return from a forward navigation
 * that merely looks like one. The router writes its own index into
 * `history.state`, so filling in the paths beside it is enough.
 *
 * Distinct from `NavigationHistory`, which models the *logical* back the
 * product wants — it splices entries out and collapses repeats, so it drifts
 * from the browser stack on purpose. This service only mirrors the browser.
 */
class BrowserHistoryEntriesService {
  private paths = new Map<number, string>();

  /** Position of the current entry, or `null` outside the browser. */
  getCurrentIndex(): number | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const index = (window.history.state as { idx?: number } | null)?.idx;

    return typeof index === 'number' ? index : null;
  }

  /**
   * Notes the path of the entry the reader is on.
   *
   * A new navigation drops every forward entry, so those are forgotten here
   * too — otherwise a reused index would hand back the path of a page that no
   * longer sits there.
   */
  record(pathname: string): void {
    const index = this.getCurrentIndex();

    if (index === null) {
      return;
    }

    for (const known of this.paths.keys()) {
      if (known > index) {
        this.paths.delete(known);
      }
    }

    this.paths.set(index, pathname);
  }

  /**
   * Whether going back one entry lands on `pathname`.
   *
   * Compared without the query string: the catalog rewrites its own search
   * params in place, so the entry behind the reader may carry a different query
   * than the one the logical stack remembers while still being the same page.
   */
  isPreviousEntry(pathname: string): boolean {
    const index = this.getCurrentIndex();

    if (index === null || index < 1) {
      return false;
    }

    return this.paths.get(index - 1) === pathname;
  }

  /**
   * How many entries back the nearest page other than `pathname` sits, or
   * `null` when the entries behind the reader are unknown or are all that page.
   *
   * A modal pushes an entry of its own and closes by replacing it, so a page
   * that has opened one sits in the history twice in a row, and a single step
   * back lands on the very same page.
   */
  stepsBackToLeave(pathname: string): number | null {
    const index = this.getCurrentIndex();

    if (index === null) {
      return null;
    }

    for (let previous = index - 1; previous >= 0; previous--) {
      const path = this.paths.get(previous);

      if (path === undefined) {
        return null;
      }

      if (path !== pathname) {
        return index - previous;
      }
    }

    return null;
  }
}

export const browserHistoryEntries = new BrowserHistoryEntriesService();
export default browserHistoryEntries;
